/**
 * Domain Configuration - Single Source of Truth
 * 
 * All domain-related URLs are derived from these base configurations.
 * This ensures consistency and makes environment switching reliable.
 */

export type Environment = 'development' | 'production' | 'test'

// Base domain configuration - SINGLE SOURCE OF TRUTH
const DOMAIN_CONFIG = {
  development: {
    baseDomain: 'tn-xp.eclab.pro',
    subdomain: 'dev'
  },
  production: {
    baseDomain: 'tn-xp.eclab.pro',
    subdomain: null // No subdomain for production
  },
  test: {
    baseDomain: 'localhost',
    subdomain: null,
    port: 3001
  }
} as const

/**
 * Get the current environment from Vite environment variables
 */
export function getCurrentEnvironment(): Environment {
  // Single selector: VITE_ENVIRONMENT controls everything
  const raw = (import.meta.env.VITE_ENVIRONMENT || '').toString().toLowerCase()
  if (raw === 'development' || raw === 'dev') return 'development'
  if (raw === 'test') return 'test'
  return 'production'
}

/**
 * Domain URL generation functions - All URLs derived from base config
 */
export class DomainManager {
  private env: Environment
  private config: typeof DOMAIN_CONFIG[Environment]

  constructor(environment?: Environment) {
    this.env = environment || getCurrentEnvironment()
    this.config = DOMAIN_CONFIG[this.env]
  }

  /**
   * Get the frontend domain (where the app is served)
   * Examples:
   * - dev: https://dev.tn-xp.eclab.pro
   * - prod: https://tn-xp.eclab.pro  
   * - test: http://localhost:3001
   */
  getFrontendDomain(): string {
    if (this.env === 'test') {
      return `http://${this.config.baseDomain}:${(this.config as any).port}`
    }
    
    const domain = this.config.subdomain 
      ? `${this.config.subdomain}.${this.config.baseDomain}`
      : this.config.baseDomain
    
    return `https://${domain}`
  }

  /**
   * Get the API base URL
   * Uses dedicated API subdomain as per Terraform configuration
   */
  getApiBaseUrl(): string {
    if (this.env === 'test') {
      return `http://${this.config.baseDomain}:3000/api`
    }
    const apiDomain = this.config.subdomain
      ? `api.${this.config.subdomain}.${this.config.baseDomain}` // dev → api.dev.base
      : `api.${this.config.baseDomain}`                          // prod → api.base
    return `https://${apiDomain}`
  }

  /**
   * Get the OAuth callback URL
   * Must match TiendaNube app configuration
   */
  getOAuthCallbackUrl(): string {
    const frontendDomain = this.getFrontendDomain()
    return `${frontendDomain}/callback`
  }

  /**
   * Get the tracking script URL
   */
  getTrackingScriptUrl(): string {
    if (this.env === 'test') {
      return '/tnx-funnel-tracker.js'
    }
    
    // Served from same origin (CloudFront)
    return '/tnx-funnel-tracker.js'
  }

  /**
   * Get environment info for debugging
   */
  getEnvironmentInfo() {
    return {
      environment: this.env,
      baseDomain: this.config.baseDomain,
      subdomain: this.config.subdomain,
      frontendDomain: this.getFrontendDomain(),
      apiBaseUrl: this.getApiBaseUrl(),
      oauthCallback: this.getOAuthCallbackUrl(),
      trackingScript: this.getTrackingScriptUrl()
    }
  }
}

// Export singleton instance
export const domainManager = new DomainManager()

// Export helper functions for backward compatibility
export const getDomainConfig = () => domainManager.getEnvironmentInfo()
export const getFrontendDomain = () => domainManager.getFrontendDomain()
export const getApiBaseUrl = () => domainManager.getApiBaseUrl()
export const getOAuthCallbackUrl = () => domainManager.getOAuthCallbackUrl()
export const getTrackingScriptUrl = () => domainManager.getTrackingScriptUrl()
